import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { throttle } from 'lodash-es';
import { Icon } from '@fuxi/eevee-icon';
import dayjs from 'dayjs';
import { Player } from 'video-react';
import { Skeleton, Image } from 'antd';
import { Header, ScrollView, SlideSelect } from '@/components';
import { useNav } from '@/hooks';
import spinImg from '@/assets/image/spin-gray.png';
import videoFallbackImg from '@/assets/image/video-fallback.png';
import emptyImg from '@/assets/image/order-empty.png';
import { ABNORMAL_ORDER_STATE, ALL_PAGES, COMMON_VIDEO_POSTER, DAYJS_FORMAT } from '@/constant';
import service from '@/service';
import { ScrollViewRefAttrs } from '@/components/scroll-view';

import cx from './index.module.scss';

interface Props {}

type FeedbackTab = 'all' | 'to-be-processing' | 'in-progress' | 'completed';

export const getMediaFormat = (url: string) => {
  if (!url) return;
  return url?.split('?')[1]?.split('=')[1] || 'image';
};

const Tab2State: Record<FeedbackTab, Service.AbnormalOrderState[]> = {
  all: [],
  'to-be-processing': ['reported'],
  'in-progress': ['accepted', 'processing', 'feedback'],
  completed: ['completed', 'communication'],
};

type GetAbnormalOrderList = GetAbnormalOrderListParams & { reset?: boolean };

const renderTime = (time: number) => (time ? dayjs(time).format(DAYJS_FORMAT.MINUTES) : '');

const ALL_TABS = [
  {
    value: 'all',
    label: '全部',
  },
  {
    value: 'to-be-processing',
    label: '待处理',
  },
  {
    value: 'in-progress',
    label: '处理中',
  },
  {
    value: 'completed',
    label: '已完成',
  },
];

const FeedbackProgress: React.FC<Props> = () => {
  const nav = useNav();
  const [selectTab, setSelectTab] = useState<FeedbackTab>('all');
  const [showDetailsIds, setShowDetailsIds] = useState([]);
  const [isLoadingOrder, setIsLoadingOrder] = useState(true);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [abnormalOrderList, setAbnormalOrderList] = useState<GetAbnormalOrderListResponse['data']>([]);
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const [previewIndex, setPreviewIndex] = useState(0);
  const playerRef = useRef(null);

  const getAbnormalOrderList = useCallback(async (params: GetAbnormalOrderList) => {
    const { current = 1, pageSize = 6, states, reset = false } = params || {};
    try {
      setIsLoadingOrder(true);
      const { data } = await service.service.getAbnormalOrderList({
        current,
        pageSize,
        states,
      });

      setAbnormalOrderList(reset ? data?.data : prev => [...prev, ...(data?.data || [])]);

      const _hasMoreData = data?.data?.length >= pageSize;

      setHasMoreData(_hasMoreData);
      if (_hasMoreData) {
        setTimeout(() => scrollViewRef.current?.bsInstance?.finishPullUp(), 300);
      }
    } finally {
      setIsLoadingOrder(false);
    }
  }, []);

  const loadMore = useMemo(
    () =>
      throttle(async () => {
        // 当前数据量小于8则说明没有更多数据
        if (abnormalOrderList?.length < 8) return;

        getAbnormalOrderList({
          current: abnormalOrderList?.length / 8 + 1,
          pageSize: 8,
          states: Tab2State[selectTab],
        });
      }, 600),
    [abnormalOrderList, getAbnormalOrderList, selectTab]
  );

  useEffect(() => {
    getAbnormalOrderList({
      current: 1,
      pageSize: 8,
      states: Tab2State[selectTab],
      reset: true,
    });
    setAbnormalOrderList([]);
    setShowDetailsIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectTab]);

  useEffect(() => {
    if (abnormalOrderList?.length || showDetailsIds?.length) {
      scrollViewRef.current?.handleRefresh();
    }
  }, [selectTab, abnormalOrderList?.length, showDetailsIds?.length]);

  return (
    <div className={cx('FeedbackProgress')}>
      <Header backConfig={{ text: '返回', action: () => nav(ALL_PAGES.HELP_FEEDBACK) }} title="反馈进度" />
      <SlideSelect
        className={cx('slide-select')}
        value={selectTab}
        onChange={value => setSelectTab(value as any)}
        steps={ALL_TABS}
      />
      {!!abnormalOrderList?.length ? (
        <div className={cx('feedback-container')}>
          <ScrollView className={cx('feedback-wrapper')} ref={scrollViewRef} onPullingUp={loadMore}>
            <div className={cx('feedback-list')}>
              {abnormalOrderList.map(item => (
                <div key={item.id} className={cx('feedback-item')}>
                  <div
                    className={cx('arrow')}
                    onClick={() => {
                      const isExpand = showDetailsIds?.includes(item.id);
                      if (isExpand) {
                        setShowDetailsIds(showDetailsIds.filter(id => id !== item.id));
                      } else {
                        setShowDetailsIds([...showDetailsIds, item.id]);
                      }
                    }}
                  >
                    <Icon
                      name={showDetailsIds?.includes(item.id) ? 'arrow-down-thin' : 'arrow-right-thin'}
                      color="#333333"
                      size={20}
                    />
                  </div>
                  <div className={cx('content')}>
                    <div
                      className={cx('main-info')}
                      onClick={() => {
                        const isExpand = showDetailsIds?.includes(item.id);
                        if (isExpand) {
                          setShowDetailsIds(showDetailsIds.filter(id => id !== item.id));
                        } else {
                          setShowDetailsIds([...showDetailsIds, item.id]);
                        }
                      }}
                    >
                      <div className={cx('left')}>
                        <div className={cx('header')}>
                          <div className={cx('id')}>{item.id}</div>
                          <div className={cx('plan-name')}>{item.goodsName || '建议与反馈'}</div>
                        </div>
                        <div className={cx('feedback-time')}>上报时间：{renderTime(item.createTime)}</div>
                      </div>
                      <div className={cx('right')}>
                        <div className={cx('order-state')}>
                          <div className={cx('dot', item.state)} />
                          <div className={cx('state-text')}>{ABNORMAL_ORDER_STATE[item.state]}</div>
                        </div>
                        {item.state === 'feedback' && (
                          <div className={cx('feedback-tip')}>请在预留联系方式中查看短信确认反馈结果</div>
                        )}
                      </div>
                    </div>
                    <div className={cx('details-info', { show: showDetailsIds?.includes(item.id) })}>
                      {!!item.abnormalTime && (
                        <div className={cx('abnormal-time')}>问题出现时间：{renderTime(item.abnormalTime)}</div>
                      )}
                      <div className={cx('issue-desc')}>
                        <div className={cx('label')}>问题描述：</div>
                        <div className={cx('text')}>{item?.remark}</div>
                      </div>
                      <div className={cx('media-box')}>
                        <Image.PreviewGroup
                          items={item.imageUrls}
                          preview={{
                            movable: false,
                            current: previewIndex,
                            onChange: index => setPreviewIndex(index),
                            onVisibleChange: visible => {
                              if (!visible) {
                                playerRef.current?.actions?.pause?.();
                              }
                            },
                            imageRender: (node, info) => {
                              // @ts-ignore
                              if (getMediaFormat(info?.image?.url) === 'image') {
                                return node;
                              } else {
                                return (
                                  <Player
                                    ref={playerRef}
                                    poster={COMMON_VIDEO_POSTER}
                                    // @ts-ignore
                                    className={cx('custom-video-player')}
                                    // @ts-ignore
                                    src={info?.image?.url}
                                    aspectRatio="16:9"
                                  />
                                );
                              }
                            },
                            toolbarRender: () => null,
                          }}
                        >
                          {item.imageUrls?.map((item, index) => (
                            <Fragment key={item}>
                              {getMediaFormat(item) === 'image' ? (
                                <Image onClick={() => setPreviewIndex(index)} src={item} alt="" />
                              ) : (
                                <Image onClick={() => setPreviewIndex(index)} src={videoFallbackImg} alt="" />
                              )}
                            </Fragment>
                          ))}
                        </Image.PreviewGroup>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div className={cx('load-data-info')}>
                {hasMoreData ? (
                  <div className={cx('has-more-data')}>
                    <img className={cx('img-box')} src={spinImg} alt="" />
                    <div>加载中</div>
                  </div>
                ) : (
                  <div className={cx('show-tips')}>-最多显示近半年问题反馈订单-</div>
                )}
              </div>
            </div>
          </ScrollView>
        </div>
      ) : (
        <Skeleton
          className={cx('empty-skeleton')}
          paragraph={{
            rows: 8,
            width: '100%',
          }}
          title={false}
          loading={isLoadingOrder}
        >
          <div className={cx('empty')}>
            <img className={cx('empty-img')} src={emptyImg} alt="" />
            <div>暂无问题记录</div>
          </div>
        </Skeleton>
      )}
    </div>
  );
};

export default FeedbackProgress;
